/**
 * The guides a customer drags off the rulers.
 *
 * Theirs, not the product's: lines to work to, saved with the design, and never
 * printed - the press file and the proof know nothing about them. Objects snap
 * to them exactly as they snap to the page's centre (snapping.ts), so a guide
 * is a place to line things up rather than a decoration.
 *
 * Teal, because the accent means selection and the snap hints are blue. A guide
 * has to be distinguishable from the hint that appears when something lands on
 * it, or the two read as one flickering line.
 *
 * Dragging one moves it; dragging it onto a ruler throws it away, which is what
 * every design tool does and what the customer will try first.
 */

import { useRef } from 'react';
import { Group, Line } from 'react-konva';
import type Konva from 'konva';
import { useEditor } from '../state/store';
import { guidesOf, type Page } from '../model/types';
import { mmToPx, pxToMm } from '../model/units';
import { snapGuide } from './snapping';
import { RULER } from './Rulers';

const GUIDE = '#00a3a3';

/** How far a guide's line runs past the page, in mm, so it crosses the view. */
const OVERRUN = 2000;

export function RulerGuides({ zoom }: { zoom: number }) {
  const doc = useEditor((s) => s.doc);
  const page = useEditor((s) => s.doc?.pages[s.pageIndex] ?? null);
  const view = useEditor((s) => s.view);
  const drafting = useEditor((s) => s.drafting);

  if (!doc || !page || view.preview || !view.showGuides) return null;

  const guides = guidesOf(page);

  return (
    <Group>
      {(['x', 'y'] as const).map((axis) =>
        guides[axis].map((at, index) => (
          <GuideLine key={`${axis}-${index}`} axis={axis} at={at} index={index} page={page} safe={doc.safe} zoom={zoom} />
        ))
      )}

      {/* The one being dragged off a ruler, before it is placed. */}
      {drafting && <Line {...points(drafting.axis, drafting.at, zoom)} stroke={GUIDE} strokeWidth={1} strokeScaleEnabled={false} dash={[6, 4]} listening={false} />}
    </Group>
  );
}

function points(axis: 'x' | 'y', at: number, zoom: number) {
  const on = mmToPx(at, zoom);
  const far = mmToPx(OVERRUN, zoom);

  return { points: axis === 'x' ? [on, -far, on, far] : [-far, on, far, on] };
}

function GuideLine({
  axis,
  at,
  index,
  page,
  safe,
  zoom,
}: {
  axis: 'x' | 'y';
  at: number;
  index: number;
  page: Page;
  safe: number;
  zoom: number;
}) {
  /*
   * Where the line was when the drag began, in absolute stage pixels.
   *
   * Konva hands dragBoundFunc absolute coordinates, so "do not move on the
   * other axis" means "keep that coordinate", not "set it to zero" - which
   * threw the line to the corner of the page.
   */
  const from = useRef<{ x: number; y: number } | null>(null);

  const onDragStart = (event: Konva.KonvaEventObject<DragEvent>) => {
    from.current = event.target.getAbsolutePosition();
  };

  /*
   * The line follows the pointer, and the page is written once at the end.
   *
   * Konva reports the whole travel since the drag began, not the step since
   * the last event, so moving the guide on every event and then rebasing the
   * node added that travel to itself over and over - a 10 mm drag threw the
   * guide off the sheet.
   */
  const onDragMove = (event: Konva.KonvaEventObject<DragEvent>) => {
    const node = event.target;
    const travelled = pxToMm(axis === 'x' ? node.x() : node.y(), zoom);
    const snapped = snapGuide(axis, at + travelled, page, safe, zoom);
    const settled = mmToPx(snapped.at - at, zoom);

    useEditor.getState().setGuides(snapped.guides);

    // Held to where it will actually land, so the line under the pointer is
    // the line that gets saved.
    node.position(axis === 'x' ? { x: settled, y: 0 } : { x: 0, y: settled });
  };

  /** Let go: written to the page, or thrown away if it was dropped on a ruler. */
  const onDragEnd = (event: Konva.KonvaEventObject<DragEvent>) => {
    const node = event.target;
    const store = useEditor.getState();
    const landed = at + pxToMm(axis === 'x' ? node.x() : node.y(), zoom);

    store.setGuides([]);
    node.position({ x: 0, y: 0 });

    const pointer = node.getStage()?.getPointerPosition();

    if (pointer && (pointer.x < RULER || pointer.y < RULER)) {
      store.removeGuide(axis, index);

      return;
    }

    store.moveGuide(axis, index, snapGuide(axis, landed, page, safe, zoom).at);
  };

  return (
    <Line
      {...points(axis, at, zoom)}
      stroke={GUIDE}
      strokeWidth={1}
      strokeScaleEnabled={false}
      hitStrokeWidth={8}
      draggable
      dragBoundFunc={(pos) => (axis === 'x' ? { x: pos.x, y: from.current?.y ?? pos.y } : { x: from.current?.x ?? pos.x, y: pos.y })}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onMouseEnter={(event) => {
        const stage = event.target.getStage();

        if (stage) stage.container().style.cursor = axis === 'x' ? 'ew-resize' : 'ns-resize';
      }}
      onMouseLeave={(event) => {
        const stage = event.target.getStage();

        if (stage) stage.container().style.cursor = '';
      }}
    />
  );
}
